import React, { useState } from 'react';
import { Theme } from '@mui/material/styles';
import {
  Box,
  Icon,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { ISectionBlockProps } from './interface/sectionBlock.interface';
import HelpKitModal from '@/components/helpkit/helpkitModal';

const useStyles = (theme: Theme) => ({
  sectionBox: { mb: 4 },
  header: { display: 'flex', alignItems: 'center', mb: 2 },
  accent: {
    width: 4,
    borderRadius: '4px',
    background: `linear-gradient(180deg, ${theme.palette.custom.danger}, ${theme.palette.custom.dangerLight})`,
    mr: 1.5,
    flexShrink: 0,
  },
  title: { color: theme.palette.custom.gray900, letterSpacing: '-0.2px' },
  subtitle: { color: 'text.secondary' },
  sectionCard: {
    p: 2.5,
    borderRadius: 3,
    border: `1px solid ${theme.palette.custom.gray100}`,
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
  },
  helpButton: {
    color: theme.palette.custom.gray400,
    '&:hover': { color: theme.palette.custom.danger },
  },
  helpIcon: {
    fontSize: 20,
  },
});

const SectionBlock = ({
  title,
  subtitle,
  children,
  card = false,
  sectionBoxSx,
  sectionCardSx,
  sectionKey,
  tooltipTitle = 'Learn more',
  hideBorderAccent = false,
  titleSx,
  titleProps,
}: ISectionBlockProps) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const [helpOpen, setHelpOpen] = useState(false);

  return (
    <>
      <Box sx={{ ...classes.sectionBox, ...sectionBoxSx }}>
        {(title || subtitle) && (
          <Box sx={{ ...classes.header, ...sectionBoxSx }}>
            {!hideBorderAccent && <Box sx={{ ...classes.accent, height: subtitle ? 44 : 22 }} />}
            <Box>
              {title && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    {...titleProps}
                    sx={{ ...classes.title, ...titleSx }}
                  >
                    {title}
                  </Typography>
                  {sectionKey && (
                    <span
                      onClick={(e) => e.stopPropagation()}
                      onFocus={(e) => e.stopPropagation()}
                    >
                      <Tooltip title={tooltipTitle} arrow placement="right">
                        <IconButton
                          size="small"
                          onClick={() => setHelpOpen(true)}
                          sx={classes.helpButton}
                        >
                          <Icon sx={classes.helpIcon}>info_outline</Icon>
                        </IconButton>
                      </Tooltip>
                    </span>
                  )}
                </Box>
              )}
              {subtitle && (
                <Typography variant="body2" sx={classes.subtitle}>
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>
        )}
        {card ? (
          <Paper elevation={0} sx={{ ...classes.sectionCard, ...sectionCardSx }}>
            {children}
          </Paper>
        ) : (
          children
        )}
      </Box>
      {sectionKey && helpOpen && (
        <span onClick={(e) => e.stopPropagation()} onFocus={(e) => e.stopPropagation()}>
          <HelpKitModal
            open={helpOpen}
            onClose={() => setHelpOpen(false)}
            sectionKey={sectionKey}
          />
        </span>
      )}
    </>
  );
};

export default SectionBlock;
